import { Container, Graphics, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS, WEAPON_CATEGORY_COLOR } from '@/config/colors';
import { ECONOMY } from '@/config/economy';
import { MAX_ACTIVE_WEAPONS, WEAPONS } from '@/config/weapons';
import { weaponGlyphKey } from '@/config/weapon-visuals';
import { t } from '@/i18n';
import { drawCardIcon } from './card-icons';
import { arrowGlyph, bagGlyph, deltaGlyph } from './hangar-icons';
import { compareEquip, shipPower, socketNumbers, socketPower } from './shop-compare';
import {
  BAG_C, COMPARE_C, CRATE_C, bagCellPos, socketX,
} from './shop-compact-layout';
import {
  compactBagBuy, compactBagTile, compactCrate, compactEmptyBag, compactSocket,
} from './shop-compact-tiles';
import type { HangarHooks } from './shop-hangar';
import { bagWeapons, type FocusItem, type ShopState } from './shop-types';
import { chamferPanel, hexPath, label } from './widgets';

/*
 * Phone HANGAR page (polish 1): sockets 1-2-3 + the compare strip on the
 * left, the bag (+ padlock) on the right, the swap-or-scrap crate at the
 * far right. Same interaction as the desktop hangar (shop-hangar-input).
 */

const BLOCK_X = 92;
const BLOCK_W = 64;

function signed(n: number, decimals: number): string {
  const v = decimals > 0 ? n.toFixed(decimals) : String(Math.round(n));
  return n >= 0 ? `+${v}` : v;
}

/** One stat column: glyph, BIG value, ▲/▼ delta (null = no compare). */
function block(
  c: Container, i: number, glyph: (g: Graphics) => void, value: number, delta: number | null,
  decimals: number,
): void {
  const cx = BLOCK_X + BLOCK_W * i + BLOCK_W / 2;
  const g = new Graphics();
  glyph(g);
  g.position.set(cx, 20);
  c.addChild(g);
  const v = label(decimals > 0 ? value.toFixed(decimals) : String(Math.round(value)), 18, 0xffffff, 1);
  v.anchor.set(0.5);
  v.position.set(cx, delta === null ? 56 : 48);
  if (v.width > BLOCK_W - 4) v.scale.set((BLOCK_W - 4) / v.width);
  c.addChild(v);
  if (delta === null) return;
  const flat = Math.abs(delta) < (decimals > 0 ? 0.05 : 0.5);
  const color = flat ? 0x8a9099 : delta > 0 ? COLORS.statUp : COLORS.statDown;
  const d = new Graphics();
  if (!flat) deltaGlyph(d, cx - 18, 76, 6, color, delta > 0);
  c.addChild(d);
  const dt = label(flat ? '=' : signed(delta, decimals), 14, color, 0);
  dt.anchor.set(0, 0.5);
  dt.position.set(cx - 10, 76);
  if (dt.width > BLOCK_W / 2 + 18) dt.scale.set((BLOCK_W / 2 + 18) / dt.width);
  c.addChild(dt);
}

const powerG = (g: Graphics): void => void drawCardIcon(g, 'damage', COLORS.statPower).scale.set(0.42);
const rateG = (g: Graphics): void => void drawCardIcon(g, 'firerate', COLORS.statTempo).scale.set(0.42);
const areaG = (g: Graphics): void => {
  const col = WEAPON_CATEGORY_COLOR.bomb;
  g.circle(0, 0, 4).fill(col);
  g.circle(0, 0, 8).stroke({ color: col, width: 2 });
  g.circle(0, 0, 12).stroke({ color: col, width: 2, alpha: 0.6 });
};

function weaponGlyph(c: Container, id: string, x: number, scale: number): void {
  const glyph = new Sprite(getTexture(weaponGlyphKey(id)));
  glyph.anchor.set(0.5);
  glyph.scale.set(scale);
  glyph.position.set(x, COMPARE_C.h / 2);
  c.addChild(glyph);
}

/** Compare strip: bag weapon → socket n with POWER / RATE / AREA deltas. */
function compactCompare(s: ShopState, focus: HangarHooks['compare']): Container {
  const { w, h } = COMPARE_C;
  const c = new Container();
  c.addChild(chamferPanel(new Graphics(), w, h, { fill: 0x0f0c1a, stroke: 0x2a2440, strokeWidth: 2, notch: 10 }));
  if (focus && 'weaponId' in focus) {
    if (!WEAPONS[focus.weaponId]) return c;
    weaponGlyph(c, focus.weaponId, 24, 0.5);
    const m = arrowGlyph(new Graphics(), 50, h / 2, 7, 0xe8eaf0);
    hexPath(m, 72, h / 2, 13).fill(COLORS.playerYellow);
    const n = label(String(focus.socket + 1), 15, 0x0f0c1a, 0);
    n.anchor.set(0.5);
    n.position.set(72, h / 2 + 1);
    c.addChild(m, n);
    const { before: b, after: a } = compareEquip(s, focus.weaponId, focus.socket);
    block(c, 0, powerG, a.power, a.power - b.power, 0);
    block(c, 1, rateG, a.rate, a.rate - b.rate, 1);
    block(c, 2, areaG, a.area, a.area - b.area, 0);
    return c;
  }
  const id = focus ? s.equipped[focus.socket] ?? null : null;
  if (id) {
    weaponGlyph(c, id, 46, 0.55);
    const one = socketNumbers(s, s.equipped, id);
    block(c, 0, powerG, one.dps, null, 0);
    block(c, 1, rateG, one.rate, null, 1);
    block(c, 2, areaG, one.area, null, 0);
    return c;
  }
  const head = new Graphics();
  drawCardIcon(head, 'damage', COLORS.statPower).scale.set(0.62);
  head.position.set(38, h / 2);
  const total = label(String(Math.round(shipPower(s, s.equipped))), 28, 0xffffff, 1);
  total.anchor.set(0, 0.5);
  total.position.set(62, h / 2);
  const cap = label(t('shop.shipPower'), 13, 0x8a9099, 2);
  cap.anchor.set(0, 0.5);
  cap.position.set(74 + total.width, h / 2 + 3);
  if (cap.x + cap.width > w - 10) cap.scale.set(Math.max(0.5, (w - 10 - cap.x) / cap.width));
  c.addChild(head, total, cap);
  return c;
}

/** The whole HANGAR page, page-local (the caller places it at MAIN_Y). */
export function buildCompactHangar(s: ShopState, hooks: HangarHooks): Container {
  const c = new Container();
  for (let i = 0; i < MAX_ACTIVE_WEAPONS; i++) {
    const item: FocusItem = { kind: 'socket', index: i };
    const open = i < s.equipCapacity;
    const weaponId = open ? s.equipped[i] ?? null : null;
    const price = !open && i === s.equipCapacity ? s.socketPrice : null;
    const tile = compactSocket({
      index: i, weaponId, open, price,
      affordable: price !== null && s.red >= price,
      power: weaponId ? socketPower(s, i) : 0,
      selected: hooks.isSel(item),
      target: hooks.targetSocket === i,
      fresh: weaponId !== null && s.fresh.includes(weaponId),
    });
    tile.position.set(socketX(i), 0);
    if (open || price !== null) hooks.interactive(tile, item);
    c.addChild(tile);
  }
  const cmp = compactCompare(s, hooks.compare);
  cmp.position.set(0, COMPARE_C.y);
  c.addChild(cmp);

  // Bag header: sack + count (orange when full)
  const bag = bagWeapons(s);
  const full = bag.length >= s.bagCap;
  c.addChild(bagGlyph(new Graphics(), BAG_C.x + 10, 12, 9, full ? COLORS.dangerOrange : COLORS.playerCream));
  const count = label(`${bag.length}/${s.bagCap}`, 16, full ? COLORS.dangerOrange : 0xe8eaf0, 1);
  count.anchor.set(0, 0.5);
  count.position.set(BAG_C.x + 26, 12);
  c.addChild(count);

  const cells = Math.min(ECONOMY.inventory.total, s.bagCap + (s.bagPrice !== null ? 1 : 0));
  for (let i = 0; i < cells; i++) {
    const id = bag[i];
    let tile: Container;
    if (id) {
      const item: FocusItem = { kind: 'weapon', weaponId: id };
      const sel = hooks.isSel(item);
      const { before, after } = compareEquip(s, id, hooks.targetFor(id));
      tile = compactBagTile({
        weaponId: id, selected: sel, fresh: s.fresh.includes(id), delta: after.power - before.power,
        showScrap: hooks.scrapBadges || sel, armed: hooks.armed === id,
      }, () => hooks.scrap(id));
      hooks.interactive(tile, item);
    } else if (i < s.bagCap) {
      tile = compactEmptyBag();
    } else {
      const item: FocusItem = { kind: 'bagBuy' };
      tile = compactBagBuy(s.bagPrice!, s.yellow >= s.bagPrice!, hooks.isSel(item));
      hooks.interactive(tile, item);
    }
    const p = bagCellPos(i);
    tile.position.set(p.x, p.y);
    c.addChild(tile);
  }

  if (s.pending) {
    const id = s.pending;
    const item: FocusItem = { kind: 'weapon', weaponId: id };
    const tile = compactCrate(id, hooks.targetFor(id), hooks.isSel(item), () => hooks.swap(id), () => hooks.scrap(id));
    tile.position.set(CRATE_C.x, 0);
    hooks.interactive(tile, item);
    c.addChild(tile);
  }
  return c;
}
